import React, {useEffect, useState} from "react";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAuth from "../../../../hooks/useAuth";
import {toastError} from "../../../../helpers/Toast";

const EmployeeTrainingManagementCard = ({employeeId}) => {
  const axiosPrivate = useAxiosPrivate();
  const {auth} = useAuth();
  const roles = Array.isArray(auth?.roles) ? auth.roles : String(auth?.roles || "").replace(/[\[\]\s]/g, "").split(",");
  const isHr = roles.includes("ROLE_HR") || roles.includes("ROLE_ADMIN");
  const canTasks = isHr || roles.includes("ROLE_MANAGER");
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
  const [budget, setBudget] = useState(null);
  const [budgetValue, setBudgetValue] = useState("");
  const [tasks, setTasks] = useState([]);
  const [task, setTask] = useState({title: "", description: "", dueDate: ""});

  const load = async () => {
    if (!employeeId || !canTasks) return;
    try {
      const requests = [axiosPrivate.get(`/employee-training-tasks/employee/${employeeId}`)];
      if (isHr) requests.push(axiosPrivate.get(`/employee-training-budgets/${employeeId}/${year}`));
      const responses = await Promise.all(requests);
      setTasks(responses[0].data || []);
      if (isHr) {
        setBudget(responses[1].data);
        setBudgetValue(responses[1].data?.budgetAmount ?? 0);
      }
    } catch (e) {
      toastError("Nie udało się pobrać danych rozwoju pracownika");
    }
  };

  useEffect(() => { load(); }, [employeeId, canTasks, isHr, year]);

  const saveBudget = async () => {
    try {
      const response = await axiosPrivate.put(`/employee-training-budgets/${employeeId}/${year}`, {budgetAmount: Number(budgetValue || 0)});
      setBudget(response.data);
    } catch (e) {
      toastError(e?.response?.data?.message || e?.response?.data || "Nie udało się zapisać budżetu szkoleniowego");
    }
  };

  const addTask = async () => {
    if (!task.title.trim()) {
      toastError("Podaj nazwę zadania");
      return;
    }
    try {
      await axiosPrivate.post("/employee-training-tasks", {...task, employeeId});
      setTask({title: "", description: "", dueDate: ""});
      await load();
    } catch (e) {
      toastError("Nie udało się dodać zadania");
    }
  };

  const toggleTask = async item => {
    try {
      await axiosPrivate.put(`/employee-training-tasks/${item.id}`, {...item, completed: !item.completed});
      await load();
    } catch (e) {
      toastError("Nie udało się zaktualizować zadania");
    }
  };

  if (!canTasks || !employeeId) return null;

  return (
    <div className="card mb-3">
      <div className="card-header"><strong>Rozwój i szkolenia</strong></div>
      <div className="card-body">
        {isHr && (
          <div className="border rounded p-2 mb-3">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <strong>Budżet szkoleń</strong>
              <select className="form-select form-select-sm" style={{maxWidth: 100}} value={year} onChange={e => setYear(Number(e.target.value))}>
                {yearOptions.map(value => <option key={value} value={value}>{value}</option>)}
              </select>
              <input type="number" min="0" step="0.01" className="form-control form-control-sm" style={{maxWidth: 140}} value={budgetValue} onChange={e => setBudgetValue(e.target.value)} disabled={budget && !budget.employmentContract}/>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={saveBudget} disabled={budget && !budget.employmentContract}>Zapisz</button>
              <span className="small">Wydano: <strong>{budget?.spentAmount ?? 0}</strong>, pozostało: <strong>{budget?.remainingAmount ?? 0}</strong></span>
              {budget && !budget.employmentContract && <span className="badge bg-secondary">Budżet dotyczy pracowników na umowie o pracę</span>}
            </div>
          </div>
        )}
        <div className="row g-2 mb-2">
          <div className="col-md-4"><input className="form-control" placeholder="Karta zadania / zadanie rozwojowe" value={task.title} onChange={e => setTask({...task, title: e.target.value})}/></div>
          <div className="col-md-3"><input type="date" className="form-control" value={task.dueDate} onChange={e => setTask({...task, dueDate: e.target.value})}/></div>
          <div className="col-md-4"><input className="form-control" placeholder="Opis" value={task.description} onChange={e => setTask({...task, description: e.target.value})}/></div>
          <div className="col-md-1"><button type="button" className="btn btn-primary" onClick={addTask}>+</button></div>
        </div>
        <div className="table-responsive"><table className="table table-sm"><thead><tr><th>Zadanie</th><th>Termin</th><th>Status</th><th></th></tr></thead><tbody>
          {tasks.map(item => <tr key={item.id}><td>{item.title}<div className="small text-muted">{item.description}</div></td><td>{item.dueDate || "—"}</td><td>{item.completed ? "Wykonane" : "Otwarte"}</td><td><button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => toggleTask(item)}>{item.completed ? "Otwórz ponownie" : "Zakończ"}</button></td></tr>)}
          {!tasks.length && <tr><td colSpan="4" className="text-muted">Brak kart zadań</td></tr>}
        </tbody></table></div>
      </div>
    </div>
  );
};

export default EmployeeTrainingManagementCard;
